import { useMemo, useState } from "react";
import {
  Loader2,
  Mail,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Trash2,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  useBusinessAccounts,
  useCreateBusinessAccount,
  useDeleteBusinessAccount,
  usePatchBusinessAccount,
  useResendBusinessAccountInvite,
} from "@/hooks/useBusinesses";
import { useSessionState } from "@/hooks/useSessionState";
import { useSourcesMapping } from "@/hooks/useSources";
import { useUrlState } from "@/hooks/useUrlState";
import type { UrlStatePatch } from "@/hooks/useUrlState";
import { ApiError } from "@/services/api";
import type {
  BusinessAccount,
  CreateBusinessAccountRequest,
  PatchBusinessAccountRequest,
} from "@/services/businessApi";
import type {
  BusinessAccountsSessionState,
  BusinessAccountsUrlState,
} from "@/types/pageState";

const ALL_VALUE = "__all";
const NONE_VALUE = "__none";

const BUSINESS_TIERS = [
  { value: "0", label: "Tier 0 - unpaid" },
  { value: "1", label: "Tier 1 - eligible" },
  { value: "2", label: "Tier 2 - eligible" },
] as const;

const BUSINESS_STATUSES = ["none", "active", "past_due", "canceled"] as const;

interface BusinessFormState {
  name: string;
  email: string;
  contactName: string;
  websiteUrl: string;
  description: string;
  sourceId: string;
  subscriptionTier: string;
  subscriptionStatus: string;
  isActive: boolean;
}

const defaultBusinessForm: BusinessFormState = {
  name: "",
  email: "",
  contactName: "",
  websiteUrl: "",
  description: "",
  sourceId: NONE_VALUE,
  subscriptionTier: "0",
  subscriptionStatus: "none",
  isActive: false,
};

function formatDate(iso: string | null): string {
  if (!iso) return "None";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function statusVariant(
  status: string,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "active":
      return "default";
    case "past_due":
      return "destructive";
    default:
      return "outline";
  }
}

function handleApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    toast.error(error.message);
    return;
  }
  toast.error(error instanceof Error ? error.message : fallback);
}

function sourceLabel(
  sourceId: string | null,
  sourceNameById: Record<string, string>,
): string {
  if (!sourceId) return "No source";
  return sourceNameById[sourceId] ?? sourceId;
}

function businessFormFromBusiness(
  business: BusinessAccount,
): BusinessFormState {
  return {
    name: business.name,
    email: business.email,
    contactName: business.contact_name ?? "",
    websiteUrl: business.website_url ?? "",
    description: business.description ?? "",
    sourceId: business.source_id ?? NONE_VALUE,
    subscriptionTier: String(business.subscription_tier),
    subscriptionStatus: business.subscription_status,
    isActive: business.is_active,
  };
}

function businessPayloadFromForm(
  form: BusinessFormState,
): CreateBusinessAccountRequest {
  return {
    name: form.name.trim(),
    email: form.email.trim(),
    contact_name: form.contactName.trim() || null,
    website_url: form.websiteUrl.trim() || null,
    description: form.description.trim() || null,
    source_id: form.sourceId === NONE_VALUE ? null : form.sourceId,
    subscription_tier: Number(form.subscriptionTier),
    subscription_status: form.subscriptionStatus,
  };
}

function businessPatchFromForm(
  form: BusinessFormState,
): PatchBusinessAccountRequest {
  return {
    name: form.name.trim(),
    contact_name: form.contactName.trim() || null,
    website_url: form.websiteUrl.trim() || null,
    description: form.description.trim() || null,
    source_id: form.sourceId === NONE_VALUE ? null : form.sourceId,
    subscription_tier: Number(form.subscriptionTier),
    subscription_status: form.subscriptionStatus,
    is_active: form.isActive,
  };
}

export default function BusinessAccounts() {
  const [urlState, setUrlState] = useUrlState<BusinessAccountsUrlState>({
    schema: {
      search: "string",
      tier: "string",
      status: "string",
    },
    defaults: {},
    replace: true,
    debounceMs: 200,
  });

  const [sessionState, setSessionState] =
    useSessionState<BusinessAccountsSessionState>({
      key: "business-accounts-ui",
      defaultValue: {
        inviteDialogOpen: false,
        editBusinessId: null,
        deleteConfirmId: null,
      },
    });

  const [businessForm, setBusinessForm] =
    useState<BusinessFormState>(defaultBusinessForm);

  const accountsQuery = useBusinessAccounts();
  const sourcesMappingQuery = useSourcesMapping(true);

  const createBusiness = useCreateBusinessAccount();
  const patchBusiness = usePatchBusinessAccount();
  const resendInvite = useResendBusinessAccountInvite();
  const deleteBusiness = useDeleteBusinessAccount();

  const accounts = accountsQuery.data ?? [];
  const sourceNameById = sourcesMappingQuery.data?.mapping ?? {};
  const sourceOptions = useMemo(
    () =>
      Object.entries(sourceNameById).sort(([, a], [, b]) => a.localeCompare(b)),
    [sourceNameById],
  );

  const businessToEdit =
    sessionState.editBusinessId != null
      ? (accounts.find((a) => a.id === sessionState.editBusinessId) ?? null)
      : null;

  const businessToDelete =
    sessionState.deleteConfirmId != null
      ? (accounts.find((a) => a.id === sessionState.deleteConfirmId) ?? null)
      : null;

  const filteredAccounts = useMemo(() => {
    const search = (urlState.search ?? "").trim().toLowerCase();
    return accounts.filter((account) => {
      const matchesSearch =
        !search ||
        account.name.toLowerCase().includes(search) ||
        account.email.toLowerCase().includes(search) ||
        (account.contact_name ?? "").toLowerCase().includes(search);
      const matchesTier =
        !urlState.tier || String(account.subscription_tier) === urlState.tier;
      const matchesStatus =
        !urlState.status || account.subscription_status === urlState.status;
      return matchesSearch && matchesTier && matchesStatus;
    });
  }, [accounts, urlState.search, urlState.status, urlState.tier]);

  function openInviteDialog() {
    setBusinessForm(defaultBusinessForm);
    setSessionState((prev) => ({ ...prev, inviteDialogOpen: true }));
  }

  function openEditDialog(account: BusinessAccount) {
    setBusinessForm(businessFormFromBusiness(account));
    setSessionState((prev) => ({ ...prev, editBusinessId: account.id }));
  }

  async function submitInvite() {
    try {
      const created = await createBusiness.mutateAsync(
        businessPayloadFromForm(businessForm),
      );
      toast.success(`Invited ${created.name}.`);
      setSessionState((prev) => ({ ...prev, inviteDialogOpen: false }));
      setBusinessForm(defaultBusinessForm);
    } catch (error) {
      handleApiError(error, "Failed to invite business.");
    }
  }

  async function submitEdit() {
    if (!businessToEdit) return;
    try {
      const updated = await patchBusiness.mutateAsync({
        businessId: businessToEdit.id,
        ...businessPatchFromForm(businessForm),
      });
      toast.success(`Updated ${updated.name}.`);
      setSessionState((prev) => ({ ...prev, editBusinessId: null }));
    } catch (error) {
      handleApiError(error, "Failed to update business.");
    }
  }

  async function handleResendInvite(account: BusinessAccount) {
    try {
      await resendInvite.mutateAsync(account.id);
      toast.success(`Invite resent to ${account.email}.`);
    } catch (error) {
      handleApiError(error, "Failed to resend invite.");
    }
  }

  async function handleToggleActive(account: BusinessAccount) {
    try {
      const updated = await patchBusiness.mutateAsync({
        businessId: account.id,
        is_active: !account.is_active,
      });
      toast.success(
        updated.is_active
          ? `Activated ${updated.name}.`
          : `Deactivated ${updated.name}.`,
      );
    } catch (error) {
      handleApiError(error, "Failed to update account status.");
    }
  }

  async function handleDeleteConfirm() {
    if (sessionState.deleteConfirmId == null) return;
    const name = businessToDelete?.name ?? "business";
    try {
      await deleteBusiness.mutateAsync(sessionState.deleteConfirmId);
      toast.success(`Deleted ${name}.`);
      setSessionState((prev) => ({ ...prev, deleteConfirmId: null }));
    } catch (error) {
      handleApiError(error, "Failed to delete business.");
    }
  }

  const anyMutationPending =
    patchBusiness.isPending ||
    resendInvite.isPending ||
    deleteBusiness.isPending;

  return (
    <>
      <Toaster position="top-right" theme="dark" />
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">Business Accounts</h2>
            <p className="text-sm text-muted-foreground">
              Manage newsletter business accounts, tiers, and invitations.
            </p>
          </div>
          <Button variant="outline" onClick={openInviteDialog}>
            <Plus className="size-4" />
            Invite Business
          </Button>
        </div>

        <BusinessFilters urlState={urlState} setUrlState={setUrlState} />

        <BusinessTable
          businesses={filteredAccounts}
          isLoading={accountsQuery.isLoading}
          sourceNameById={sourceNameById}
          onEdit={openEditDialog}
          onResendInvite={handleResendInvite}
          onToggleActive={handleToggleActive}
          onDelete={(account) =>
            setSessionState((prev) => ({
              ...prev,
              deleteConfirmId: account.id,
            }))
          }
          mutationPending={anyMutationPending}
        />
      </div>

      <BusinessDialog
        title="Invite Business"
        description="Create a business account and send an invite email."
        open={sessionState.inviteDialogOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, inviteDialogOpen: open }))
        }
        form={businessForm}
        setForm={setBusinessForm}
        sourceOptions={sourceOptions}
        submitLabel="Send Invite"
        submitting={createBusiness.isPending}
        onSubmit={submitInvite}
        showActive={false}
      />

      <BusinessDialog
        title="Edit Business"
        description="Update tier, source linkage, account status, and profile details."
        open={sessionState.editBusinessId != null}
        onOpenChange={(open) =>
          setSessionState((prev) => ({
            ...prev,
            editBusinessId: open ? prev.editBusinessId : null,
          }))
        }
        form={businessForm}
        setForm={setBusinessForm}
        sourceOptions={sourceOptions}
        submitLabel="Save Business"
        submitting={patchBusiness.isPending}
        onSubmit={submitEdit}
        showActive
      />

      <Dialog
        open={sessionState.deleteConfirmId != null}
        onOpenChange={(open) =>
          setSessionState((prev) => ({
            ...prev,
            deleteConfirmId: open ? prev.deleteConfirmId : null,
          }))
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Business Account?</DialogTitle>
            <DialogDescription>
              This permanently deletes{" "}
              <strong>{businessToDelete?.name ?? "this business"}</strong> and
              all its submissions. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setSessionState((prev) => ({ ...prev, deleteConfirmId: null }))
              }
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteBusiness.isPending}
            >
              {deleteBusiness.isPending && (
                <Loader2 className="size-4 animate-spin" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BusinessFilters({
  urlState,
  setUrlState,
}: {
  urlState: BusinessAccountsUrlState;
  setUrlState: (
    updates:
      | UrlStatePatch<BusinessAccountsUrlState>
      | ((
          prev: BusinessAccountsUrlState,
        ) => UrlStatePatch<BusinessAccountsUrlState>),
  ) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="space-y-1.5">
        <Label htmlFor="business-search" className="text-xs">
          Search
        </Label>
        <Input
          id="business-search"
          className="h-8 w-64 text-sm"
          placeholder="Name, email, or contact"
          value={urlState.search ?? ""}
          onChange={(event) =>
            setUrlState((prev) => ({
              ...prev,
              search: event.target.value || undefined,
            }))
          }
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Tier</Label>
        <Select
          value={urlState.tier ?? ALL_VALUE}
          onValueChange={(value) =>
            setUrlState((prev) => ({
              ...prev,
              tier: value === ALL_VALUE ? undefined : value,
            }))
          }
        >
          <SelectTrigger className="h-8 w-40 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All tiers</SelectItem>
            {BUSINESS_TIERS.map((tier) => (
              <SelectItem key={tier.value} value={tier.value}>
                {tier.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Status</Label>
        <Select
          value={urlState.status ?? ALL_VALUE}
          onValueChange={(value) =>
            setUrlState((prev) => ({
              ...prev,
              status: value === ALL_VALUE ? undefined : value,
            }))
          }
        >
          <SelectTrigger className="h-8 w-40 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All statuses</SelectItem>
            {BUSINESS_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function BusinessTable({
  businesses,
  isLoading,
  sourceNameById,
  onEdit,
  onResendInvite,
  onToggleActive,
  onDelete,
  mutationPending,
}: {
  businesses: BusinessAccount[];
  isLoading: boolean;
  sourceNameById: Record<string, string>;
  onEdit: (business: BusinessAccount) => void;
  onResendInvite: (business: BusinessAccount) => void;
  onToggleActive: (business: BusinessAccount) => void;
  onDelete: (business: BusinessAccount) => void;
  mutationPending: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Accounts ({businesses.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Business</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Eligibility</TableHead>
              <TableHead>Invited</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-40" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Loader2 className="size-4 animate-spin" />
                </TableCell>
              </TableRow>
            ) : businesses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground">
                  No accounts match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              businesses.map((business) => (
                <TableRow key={business.id}>
                  <TableCell>
                    <div className="font-medium">{business.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {business.email}
                      {business.contact_name
                        ? ` - ${business.contact_name}`
                        : ""}
                    </div>
                  </TableCell>
                  <TableCell>
                    {sourceLabel(business.source_id, sourceNameById)}
                  </TableCell>
                  <TableCell>Tier {business.subscription_tier}</TableCell>
                  <TableCell>
                    <Badge
                      variant={statusVariant(business.subscription_status)}
                    >
                      {business.subscription_status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Badge
                        variant={business.is_active ? "default" : "outline"}
                      >
                        {business.is_active ? "Active" : "Inactive"}
                      </Badge>
                      <Badge
                        variant={business.is_eligible ? "default" : "secondary"}
                      >
                        {business.is_eligible ? "Eligible" : "Not eligible"}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(business.invited_at)}</TableCell>
                  <TableCell>{formatDate(business.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Edit"
                        onClick={() => onEdit(business)}
                        disabled={mutationPending}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Resend invite"
                        onClick={() => onResendInvite(business)}
                        disabled={mutationPending}
                      >
                        <Mail className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title={business.is_active ? "Deactivate" : "Activate"}
                        onClick={() => onToggleActive(business)}
                        disabled={mutationPending}
                      >
                        {business.is_active ? (
                          <PowerOff className="size-4" />
                        ) : (
                          <Power className="size-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Delete"
                        className="text-destructive hover:text-destructive"
                        onClick={() => onDelete(business)}
                        disabled={mutationPending}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function BusinessDialog({
  title,
  description,
  open,
  onOpenChange,
  form,
  setForm,
  sourceOptions,
  submitLabel,
  submitting,
  onSubmit,
  showActive,
}: {
  title: string;
  description: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: BusinessFormState;
  setForm: (
    value: BusinessFormState | ((prev: BusinessFormState) => BusinessFormState),
  ) => void;
  sourceOptions: [string, string][];
  submitLabel: string;
  submitting: boolean;
  onSubmit: () => void;
  showActive: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${title}-name`}>Name</Label>
            <Input
              id={`${title}-name`}
              value={form.name}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, name: event.target.value }))
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${title}-email`}>Email</Label>
            <Input
              id={`${title}-email`}
              type="email"
              value={form.email}
              disabled={showActive}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, email: event.target.value }))
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${title}-contact`}>Contact Name</Label>
            <Input
              id={`${title}-contact`}
              value={form.contactName}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  contactName: event.target.value,
                }))
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${title}-website`}>Website URL</Label>
            <Input
              id={`${title}-website`}
              value={form.websiteUrl}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, websiteUrl: event.target.value }))
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label>Linked Source</Label>
            <Select
              value={form.sourceId}
              onValueChange={(value) =>
                setForm((prev) => ({ ...prev, sourceId: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>No source</SelectItem>
                {sourceOptions.map(([sourceId, name]) => (
                  <SelectItem key={sourceId} value={sourceId}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Subscription Tier</Label>
            <Select
              value={form.subscriptionTier}
              onValueChange={(value) =>
                setForm((prev) => ({ ...prev, subscriptionTier: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_TIERS.map((tier) => (
                  <SelectItem key={tier.value} value={tier.value}>
                    {tier.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Subscription Status</Label>
            <Select
              value={form.subscriptionStatus}
              onValueChange={(value) =>
                setForm((prev) => ({ ...prev, subscriptionStatus: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showActive && (
            <div className="flex items-center gap-2 pt-7">
              <Switch
                id={`${title}-active`}
                checked={form.isActive}
                onCheckedChange={(checked) =>
                  setForm((prev) => ({ ...prev, isActive: checked }))
                }
              />
              <Label htmlFor={`${title}-active`}>Active account</Label>
            </div>
          )}
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor={`${title}-description`}>Description</Label>
            <Textarea
              id={`${title}-description`}
              value={form.description}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!form.name.trim() || !form.email.trim() || submitting}
          >
            {submitting && <Loader2 className="size-4 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
